import { FC } from 'react';
import { useTranslation } from 'react-i18next';

import MaskInput from 'components/MaskedInput';
import Input from 'components/Input';
import { unMaskInputValue } from 'utils/helpers';
import { useCroptimizer } from 'features/Croptimizer/providers/CroptimizerContext';

interface ExpectedGainsSectionProps {
  getCurrentN: string | null;
  currentNPercent: number | null;
  getCurrentP: string | null;
  currentPPercent: number | null;
  getCurrentK: string | null;
  currentKPercent: number | null;
  getTargetN: string | null;
  targetNPercent: string | null;
  getTargetP: string | null;
  getTargetPPercent: string | null;
  getTargetK: string | null;
  getTargetKPercent: string | null;
  kgHa: string;
}

const ExpectedGainsSection: FC<ExpectedGainsSectionProps> = ({
  getCurrentN,
  currentNPercent,
  getCurrentP,
  currentPPercent,
  getCurrentK,
  currentKPercent,
  getTargetN,
  targetNPercent,
  getTargetP,
  getTargetPPercent,
  getTargetK,
  getTargetKPercent,
  kgHa,
}) => {
  const { t } = useTranslation();
  const { isMaintenanceMode } = useCroptimizer();

  return (
    <div className='border-b border-primitiveGrey py-8'>
      <div className='text-black font-bold text-xl md:text-lg mb-8 break-inside-avoid'>
        {t('expectedNpkGains')}
      </div>
      <div className="text-black font-bold md:text-lg mb-6 break-inside-avoid">
        {t('npkEffiCurrentPh')} ({kgHa})
      </div>
      <div className="flex mx-[-0.5rem] mb-8 pt-2 break-inside-avoid">
        <div className="w-1/3 px-2 break-inside-avoid">
          <MaskInput
            titleClassName="top-[-9px] left-[8px] bg-disable-gradient"
            title="N"
            className="w-full"
            disabled
            isBlueBg
            value={unMaskInputValue(getCurrentN ?? '')}
          />
          <div className="text-sm mt-1">{currentNPercent?.toFixed(0)}%</div>
        </div>
        <div className="w-1/3 px-2 break-inside-avoid">
          <MaskInput
            titleClassName="top-[-9px] left-[8px] bg-disable-gradient"
            title={
              <div>
                  P<sub>2</sub>O<sub>5</sub>
              </div>
            }
            className="w-full"
            disabled
            isBlueBg
            value={unMaskInputValue(getCurrentP ?? '')}
          />
          <div className="text-sm mt-1">{currentPPercent?.toFixed(0)}%</div>
        </div>
        <div className="w-1/3 px-2 break-inside-avoid">
          <MaskInput
            titleClassName="top-[-9px] left-[8px] bg-disable-gradient"
            title={
              <div>
                  K<sub>2</sub>O
              </div>
            }
            className="w-full"
            disabled
            isBlueBg
            value={unMaskInputValue(getCurrentK ?? '')}
          />
          <div className="text-sm mt-1">{currentKPercent?.toFixed(0)}%</div>
        </div>
      </div>
      <div className="text-black font-bold md:text-lg md:mt-6 mb-8 break-inside-avoid">
        {t('npkEffiTarget')} ({kgHa})
      </div>
      {isMaintenanceMode ? (
        <div className="px-2 py-3 bg-gray-50 rounded-lg">
          <p className="text-gray-600 text-sm">
                    Pas de gain d&apos;efficacité NPK calculé si pH actuel = pH visé. Attention aux impasses, cliquer sur l&apos;onglet Strategy « Vue 5 ans » pour consulter les &apos;Pertes potentielles sur 5 ans sans entretenir le pH&apos;
          </p>
        </div>
      ) : (
        <div className="flex mx-[-0.5rem] break-inside-avoid">
          <div className="w-1/3 px-2 break-inside-avoid">
            <Input
              titleClassName="top-[-9px] left-[8px] bg-disable-gradient"
              title="N"
              className="w-full"
              disabled
              isBlueBg
              value={`+ ${getTargetN}`}
            />
            <div className="text-sm mt-1">
              {targetNPercent ? (Number(targetNPercent) * 100).toFixed(0) : '0'}%
            </div>
          </div>
          <div className="w-1/3 px-2 break-inside-avoid">
            <Input
              titleClassName="top-[-9px] left-[8px] bg-disable-gradient"
              title={
                <div>
                                P<sub>2</sub>O<sub>5</sub>
                </div>
              }
              className="w-full"
              disabled
              value={`+ ${getTargetP}`}
              isBlueBg
            />
            <div className="text-sm mt-1">
              {getTargetPPercent
                ? (Number(getTargetPPercent) * 100).toFixed(0)
                : '0'}
                        %
            </div>
          </div>
          <div className="w-1/3 px-2 break-inside-avoid">
            <Input
              titleClassName="top-[-9px] left-[8px] bg-disable-gradient"
              title={
                <div>
                                K<sub>2</sub>O
                </div>
              }
              className="w-full"
              disabled
              value={`+ ${getTargetK}`}
              isBlueBg
            />
            <div className="text-sm mt-1">
              {getTargetKPercent
                ? (Number(getTargetKPercent) * 100).toFixed(0)
                : '0'}
                        %
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default ExpectedGainsSection;
